import { apiClient, assetApi, userApi, vrAuthApi } from "./orval";
import type {
  UpdateUserRequestDto,
  UserLoginRequestDto,
  VerifyTokenRequestDto,
  ResetPasswordRequestDto,
  ResetPasswordDto,
  ChangePasswordRequestDto,
  EmailChangeDto,
} from "../generated/generated.schemas";

function asAssetArrayFromResponse(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o["$values"])) return o["$values"];
    if (Array.isArray(o.items)) return o.items;
    if (Array.isArray(o.data)) return o.data;
  }
  return [];
}

export async function verifyToken(token: string): Promise<boolean> {
  try {
    const baseURL = apiClient.defaults.baseURL ?? "http://localhost:5259";
    const response = await fetch(`${baseURL}/api/User/verify-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token } satisfies VerifyTokenRequestDto),
    });

    if (!response.ok) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

export async function loginUser(payload: UserLoginRequestDto): Promise<any> {
  const res = await userApi.postApiUserLogin(payload);
  return res.data;
}

export async function getUserById(userId: string | number): Promise<any> {
  const id = typeof userId === "string" ? Number.parseInt(userId, 10) : userId;
  const res = await userApi.getApiUserId(id);
  return res.data;
}

export async function updateUserProfile(
  userId: string | number,
  data: UpdateUserRequestDto
): Promise<any> {
  const id = typeof userId === "string" ? Number.parseInt(userId, 10) : userId;
  const res = await userApi.putApiUserId(id, data);
  return res.data;
}

export async function removeAsset(assetId: string | number): Promise<any> {
  const id = typeof assetId === "string" ? Number.parseInt(assetId, 10) : assetId;
  const res = await assetApi.deleteApiAssetAssetDeleteId(id);
  return res.data;
}

export async function getAssetsByUserToken(): Promise<any> {
  const res = await assetApi.getApiAssetGetAssetsByUserToken();
  return res.data;
}

/** All assets (curator / catalog admin views). Shape matches `getAssetsByUserToken` items. */
export async function getAllAssets(): Promise<any[]> {
  const res = await assetApi.getApiAssetGetAllAssets();
  return asAssetArrayFromResponse(res.data);
}

export async function getAssetById(assetId: string | number): Promise<any> {
  const id = typeof assetId === "string" ? Number.parseInt(assetId, 10) : assetId;
  const res = await assetApi.getApiAssetAssetUploadIdDetails(id);
  return res.data;
}

export async function validateVRCode(code: string): Promise<any> {
  const res = await vrAuthApi.postApiVrAuthValidateCode({ code });
  return res.data;
}

export async function requestResetPassword(email: string): Promise<any> {
  const payload: ResetPasswordRequestDto = { email };
  const res = await userApi.postApiUserRequestResetPassword(payload);
  return res.data;
}

export async function resetPassword(token: string, newPassword: string): Promise<any> {
  const payload: ResetPasswordDto = { token, newPassword };
  const res = await userApi.postApiUserResetPassword(payload);
  return res.data;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<any> {
  const payload: ChangePasswordRequestDto = { currentPassword, newPassword };
  const res = await userApi.postApiUserChangePassword(payload);
  return res.data;
}

export async function changeEmail(
  currentPassword: string,
  newEmail: string
): Promise<any> {
  const payload: EmailChangeDto = { currentPassword, newEmail };
  const res = await userApi.postApiUserChangeEmail(payload);
  return res.data;
}

export async function sendVerificationEmail(): Promise<any> {
  const res = await userApi.getApiUserSentVerificationEmail();
  return res.data;
}

export async function logoutUser(): Promise<any> {
  const res = await userApi.postApiUserLogoutuser();
  return res.data;
}
